import {
  SmsMarketingFailureReason as ServerFailureReason,
  SmsMarketingCodeRequestStatus,
  SmsMarketingVerifyStatus,
} from '@repo/frontend-shared';

import type {
  SmsMarketingCodeRequestResult,
  SmsMarketingFailureReason,
  SmsMarketingPendingVerification,
  SmsMarketingVerifyResult,
} from './sms-marketing-signup-types';

const FAILURE_REASONS: Record<ServerFailureReason, SmsMarketingFailureReason> = {
  [ServerFailureReason.CODE_EXPIRED]: 'code-expired',
  [ServerFailureReason.DELIVERY_FAILED]: 'delivery-failed',
  [ServerFailureReason.INVALID_CODE]: 'invalid-code',
  [ServerFailureReason.INVALID_EMAIL]: 'invalid-email',
  [ServerFailureReason.INVALID_PHONE]: 'invalid-phone',
  [ServerFailureReason.NUMBER_UNAVAILABLE]: 'number-unavailable',
  [ServerFailureReason.RATE_LIMITED]: 'rate-limited',
  [ServerFailureReason.SIGN_IN_REQUIRED]: 'sign-in-required',
  [ServerFailureReason.STALE_DISCLOSURE]: 'stale-disclosure',
  [ServerFailureReason.TOO_MANY_ATTEMPTS]: 'too-many-attempts',
};

interface PendingCodePayload {
  maskedPhone: string;
  resendAfterSeconds: number;
}

interface CodeRequestPayload {
  status: SmsMarketingCodeRequestStatus;
  failureReason: ServerFailureReason | null;
  pendingCode: PendingCodePayload | null;
}

interface VerifyPayload {
  status: SmsMarketingVerifyStatus;
  failureReason: ServerFailureReason | null;
}

function failure(reason: ServerFailureReason | null): { status: 'failed'; reason: SmsMarketingFailureReason } {
  return { status: 'failed', reason: reason ? FAILURE_REASONS[reason] : 'network' };
}

export function toPendingVerification(
  pendingCode: PendingCodePayload | null | undefined,
): SmsMarketingPendingVerification | undefined {
  return pendingCode
    ? { maskedPhone: pendingCode.maskedPhone, resendAfterSeconds: pendingCode.resendAfterSeconds }
    : undefined;
}

export function toCodeRequestResult(payload: CodeRequestPayload): SmsMarketingCodeRequestResult {
  if (payload.status === SmsMarketingCodeRequestStatus.CODE_SENT && payload.pendingCode) {
    const { maskedPhone, resendAfterSeconds } = payload.pendingCode;
    return { status: 'code-sent', maskedPhone, resendAfterSeconds };
  }
  if (payload.status === SmsMarketingCodeRequestStatus.CONFIRMED) {
    return { status: 'confirmed' };
  }
  return failure(payload.failureReason);
}

export function toVerifyResult(payload: VerifyPayload): SmsMarketingVerifyResult {
  switch (payload.status) {
    case SmsMarketingVerifyStatus.VERIFIED:
      return { status: 'verified' };
    case SmsMarketingVerifyStatus.ALREADY_ENROLLED:
      return { status: 'already-enrolled' };
    default:
      return failure(payload.failureReason);
  }
}
